import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  User,
  Shield,
  CreditCard,
  Bell,
  Smartphone,
  KeyRound,
  LogOut,
  Mail,
  Building2,
  Globe,
  Camera,
  ChevronRight,
  Check,
  Crown,
  Activity,
  Trash2,
  Lock,
} from "lucide-react";
import { LogoIcon } from "@/components/Logo";

/* ============================================================
   Mock data — só pra preview visual
   ============================================================ */
const MOCK_USER = {
  name: "Carlos Mendes",
  email: "carlos@minhaempresa.com",
  company: "Recovery Digital",
  role: "Admin",
  plan: "Pro",
  joined: "15 de Jan, 2026",
  initials: "CM",
};

/* ============================================================
   Helper compartilhado: input + label
   ============================================================ */
const Input = ({ label, value, type = "text", testid }) => (
  <div>
    <label className="block text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-2">
      {label}
    </label>
    <input
      type={type}
      defaultValue={value}
      data-testid={testid}
      className="w-full bg-cp-elevated/30 border border-white/[0.08] text-white text-sm rounded-lg px-3.5 py-2.5 focus:outline-none focus:border-cp-primary/50 transition"
    />
  </div>
);

/* ============================================================
   DESIGN #1 — CLASSIC TABS (sidebar à esquerda + conteúdo)
   Estilo: Notion / Linear / clean SaaS
   ============================================================ */
function DesignClassicTabs() {
  const [active, setActive] = useState("profile");
  const tabs = [
    { key: "profile", label: "Perfil", icon: User },
    { key: "security", label: "Segurança", icon: Shield },
    { key: "plan", label: "Plano & Cobrança", icon: CreditCard },
    { key: "notifications", label: "Notificações", icon: Bell },
    { key: "devices", label: "Dispositivos", icon: Smartphone },
    { key: "danger", label: "Zona de risco", icon: Trash2 },
  ];

  return (
    <div className="rounded-2xl border border-white/[0.06] overflow-hidden bg-cp-card/40">
      <div className="flex flex-col md:flex-row min-h-[520px]">
        {/* Sidebar */}
        <aside className="md:w-60 border-r border-white/[0.06] bg-cp-bg/40 p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-3 px-2">
            _conta
          </div>
          <nav className="space-y-0.5">
            {tabs.map((t) => {
              const Icon = t.icon;
              const isActive = active === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setActive(t.key)}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition ${
                    isActive
                      ? "bg-cp-primary/15 text-cp-primary-light"
                      : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{t.label}</span>
                  {isActive && <ChevronRight className="h-3.5 w-3.5 ml-auto" />}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Conteúdo */}
        <div className="flex-1 p-6 md:p-8">
          {active === "profile" && (
            <>
              <h3 className="font-heading text-2xl font-bold mb-1">Perfil</h3>
              <p className="text-sm text-slate-400 mb-6">
                Como você aparece na ConvertPix.
              </p>
              <div className="flex items-center gap-4 mb-8 pb-8 border-b border-white/[0.06]">
                <div className="h-16 w-16 rounded-full bg-cp-primary/15 border-2 border-cp-primary/30 flex items-center justify-center font-heading text-xl font-bold text-cp-primary-light">
                  {MOCK_USER.initials}
                </div>
                <div>
                  <button className="text-xs bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] px-3 py-1.5 rounded-md transition inline-flex items-center gap-1.5">
                    <Camera className="h-3.5 w-3.5" /> Trocar foto
                  </button>
                  <p className="text-[10px] text-slate-500 mt-1.5">JPG, PNG. Max 2MB.</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <Input label="Nome" value={MOCK_USER.name} />
                <Input label="Empresa" value={MOCK_USER.company} />
                <Input label="Email" value={MOCK_USER.email} type="email" />
                <Input label="Cargo" value={MOCK_USER.role} />
              </div>
              <div className="mt-7 flex justify-end">
                <button className="bg-cp-primary hover:bg-cp-primary-hover text-cp-bg font-semibold text-sm px-5 py-2.5 rounded-lg transition">
                  Salvar alterações
                </button>
              </div>
            </>
          )}

          {active === "security" && (
            <>
              <h3 className="font-heading text-2xl font-bold mb-1">Segurança</h3>
              <p className="text-sm text-slate-400 mb-6">
                Senha, autenticação e sessões ativas.
              </p>
              <div className="space-y-4">
                <Input label="Senha atual" value="" type="password" />
                <Input label="Nova senha" value="" type="password" />
                <Input label="Confirmar nova senha" value="" type="password" />
              </div>
              <div className="mt-6 p-4 rounded-lg border border-white/[0.06] bg-cp-elevated/20 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">Verificação em 2 etapas</div>
                  <div className="text-xs text-slate-500">Proteção extra na sua conta</div>
                </div>
                <button className="text-xs bg-white/[0.04] border border-white/[0.08] px-3 py-1.5 rounded-md">
                  Ativar
                </button>
              </div>
            </>
          )}

          {active === "plan" && (
            <>
              <h3 className="font-heading text-2xl font-bold mb-1">Plano & Cobrança</h3>
              <p className="text-sm text-slate-400 mb-6">
                Gerencie sua assinatura e ver uso do mês.
              </p>
              <div className="rounded-xl bg-gradient-to-br from-cp-primary/10 to-transparent border border-cp-primary/30 p-5 mb-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-cp-primary-light font-mono mb-1">
                      _plano atual
                    </div>
                    <div className="font-heading text-3xl font-bold flex items-center gap-2">
                      Pro <Crown className="h-5 w-5 text-cp-primary" />
                    </div>
                    <div className="text-sm text-slate-400 mt-1">
                      R$ 97/mês · próx. cobrança 15/Mar
                    </div>
                  </div>
                  <button className="bg-cp-primary text-cp-bg text-sm font-semibold px-4 py-2 rounded-lg">
                    Trocar plano
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-white/[0.06] p-4">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                    PIX monitorados
                  </div>
                  <div className="text-2xl font-bold mt-1">230 / 500</div>
                  <div className="h-1 bg-cp-elevated/40 rounded-full mt-2">
                    <div className="h-full bg-cp-primary rounded-full" style={{ width: "46%" }} />
                  </div>
                </div>
                <div className="rounded-lg border border-white/[0.06] p-4">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                    Mensagens
                  </div>
                  <div className="text-2xl font-bold mt-1">920 / 2000</div>
                  <div className="h-1 bg-cp-elevated/40 rounded-full mt-2">
                    <div className="h-full bg-cp-primary rounded-full" style={{ width: "46%" }} />
                  </div>
                </div>
              </div>
            </>
          )}

          {active !== "profile" && active !== "security" && active !== "plan" && (
            <div className="text-center py-16 text-slate-500 text-sm">
              Conteúdo da aba <span className="text-white">"{tabs.find((t) => t.key === active)?.label}"</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   DESIGN #2 — PROFILE HEADER + CARDS
   Estilo: GitHub / Vercel — header com avatar grande, depois cards
   ============================================================ */
function DesignProfileHeader() {
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-2xl border border-white/[0.06] bg-gradient-to-br from-cp-card/60 via-cp-bg to-cp-card/40 p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 h-64 w-64 bg-cp-primary/10 blur-[100px] rounded-full" />
        <div className="relative flex items-center gap-5 flex-wrap">
          <div className="relative">
            <div className="h-20 w-20 rounded-2xl bg-cp-primary/15 border-2 border-cp-primary/40 flex items-center justify-center font-heading text-3xl font-bold text-cp-primary-light">
              {MOCK_USER.initials}
            </div>
            <button className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-cp-primary text-cp-bg flex items-center justify-center hover:bg-cp-primary-hover transition">
              <Camera className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
                {MOCK_USER.name}
              </h2>
              <span className="text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded-full bg-cp-primary/15 text-cp-primary-light border border-cp-primary/30">
                {MOCK_USER.plan}
              </span>
            </div>
            <div className="text-sm text-slate-400 mt-1 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> {MOCK_USER.email}
              </span>
              <span className="text-slate-600">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" /> {MOCK_USER.company}
              </span>
            </div>
          </div>
          <button className="bg-cp-primary hover:bg-cp-primary-hover text-cp-bg font-semibold text-sm px-4 py-2.5 rounded-lg transition shrink-0">
            Editar perfil
          </button>
        </div>
      </div>

      {/* Cards grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {[
          { icon: User, title: "Dados pessoais", desc: "Nome, foto, email e empresa", action: "Editar" },
          { icon: Shield, title: "Segurança", desc: "Senha, 2FA e sessões ativas", action: "Gerenciar" },
          { icon: CreditCard, title: "Plano & Cobrança", desc: "Pro · 230/500 PIX usados este mês", action: "Ver uso" },
          { icon: Bell, title: "Notificações", desc: "WhatsApp, email e alertas", action: "Configurar" },
          { icon: Smartphone, title: "Dispositivos & Sessões", desc: "3 dispositivos conectados", action: "Ver tudo" },
          { icon: KeyRound, title: "API Keys", desc: "Acesso programático e webhooks", action: "Gerenciar" },
        ].map((card, i) => {
          const Icon = card.icon;
          return (
            <div
              key={i}
              className="rounded-xl border border-white/[0.06] bg-cp-card/40 p-5 hover:border-cp-primary/20 transition group cursor-pointer"
            >
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-lg bg-cp-elevated/40 border border-white/[0.06] flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-cp-primary-light" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold text-sm">{card.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{card.desc}</div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-600 group-hover:text-cp-primary-light transition" />
              </div>
            </div>
          );
        })}
      </div>

      <button className="w-full text-sm text-red-400 hover:text-red-300 hover:bg-red-500/5 border border-red-500/20 rounded-xl py-3.5 transition inline-flex items-center justify-center gap-2">
        <LogOut className="h-4 w-4" /> Sair da conta
      </button>
    </div>
  );
}

/* ============================================================
   DESIGN #3 — MINIMAL LINEAR (single page, sections separadas por linha)
   Estilo: Stripe / Linear — clean, denso, profissional
   ============================================================ */
function DesignMinimalLinear() {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-cp-card/40 divide-y divide-white/[0.06]">
      {/* Avatar + nome */}
      <section className="p-6 md:p-8">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-4">
          PERFIL
        </div>
        <div className="flex items-center gap-5 flex-wrap">
          <div className="h-14 w-14 rounded-full bg-cp-primary/15 border-2 border-cp-primary/30 flex items-center justify-center font-heading text-lg font-bold text-cp-primary-light">
            {MOCK_USER.initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-lg font-semibold">{MOCK_USER.name}</div>
            <div className="text-sm text-slate-500">{MOCK_USER.email}</div>
          </div>
          <button className="text-xs text-cp-primary-light hover:text-white">
            Atualizar foto
          </button>
        </div>
      </section>

      {/* Form linha a linha */}
      {[
        { label: "Nome completo", value: MOCK_USER.name, action: "Editar" },
        { label: "Email", value: MOCK_USER.email, action: "Alterar" },
        { label: "Empresa", value: MOCK_USER.company, action: "Editar" },
        { label: "Senha", value: "••••••••••", action: "Alterar" },
        { label: "Verificação 2 etapas", value: "Desativada", action: "Ativar", warn: true },
      ].map((row, i) => (
        <div
          key={i}
          className="px-6 md:px-8 py-4 flex items-center justify-between gap-4 hover:bg-white/[0.02] transition group"
        >
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono mb-1">
              {row.label}
            </div>
            <div
              className={`text-sm truncate ${
                row.warn ? "text-cp-warning" : "text-slate-100"
              }`}
            >
              {row.value}
            </div>
          </div>
          <button className="text-xs text-cp-primary-light hover:text-white border border-white/[0.08] hover:border-cp-primary/30 px-3 py-1.5 rounded-md transition shrink-0">
            {row.action}
          </button>
        </div>
      ))}

      {/* Plano */}
      <section className="px-6 md:px-8 py-5">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-3">
          ASSINATURA
        </div>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-sm">
              Plano{" "}
              <span className="font-bold text-cp-primary-light">{MOCK_USER.plan}</span>{" "}
              <span className="text-slate-500">— R$ 97/mês</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Próxima cobrança em 15 de março
            </div>
          </div>
          <div className="flex gap-2">
            <button className="text-xs border border-white/[0.08] px-3 py-1.5 rounded-md hover:bg-white/[0.04]">
              Ver fatura
            </button>
            <button className="text-xs bg-cp-primary text-cp-bg font-semibold px-3 py-1.5 rounded-md">
              Upgrade
            </button>
          </div>
        </div>
      </section>

      {/* Danger zone */}
      <section className="px-6 md:px-8 py-5 bg-red-500/[0.02]">
        <div className="text-[10px] uppercase tracking-widest text-red-400 font-mono mb-3">
          ZONA DE RISCO
        </div>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-sm text-slate-400">
            Excluir conta · ação permanente, todos os dados serão removidos
          </div>
          <button className="text-xs text-red-400 hover:text-red-300 border border-red-500/30 hover:bg-red-500/5 px-3 py-1.5 rounded-md transition">
            Excluir conta
          </button>
        </div>
      </section>
    </div>
  );
}

/* ============================================================
   DESIGN #4 — DASHBOARD STYLE (split layout — info à esquerda, ações à direita)
   Estilo: Discord / Notion settings — card grande na esquerda + ações
   ============================================================ */
function DesignDashboardStyle() {
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      {/* Card identidade */}
      <div className="lg:col-span-1 rounded-2xl border border-white/[0.06] bg-gradient-to-b from-cp-primary/[0.04] to-cp-card/40 p-6 flex flex-col items-center text-center">
        <div className="relative mb-4">
          <div className="h-24 w-24 rounded-full bg-cp-primary/15 border-2 border-cp-primary/40 flex items-center justify-center font-heading text-3xl font-bold text-cp-primary-light">
            {MOCK_USER.initials}
          </div>
          <button className="absolute bottom-0 right-0 h-8 w-8 rounded-full bg-cp-primary text-cp-bg flex items-center justify-center shadow-lg shadow-cp-primary/30 hover:bg-cp-primary-hover transition">
            <Camera className="h-4 w-4" />
          </button>
        </div>
        <h3 className="font-heading text-xl font-bold">{MOCK_USER.name}</h3>
        <div className="text-sm text-slate-400 mt-1">{MOCK_USER.email}</div>
        <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono px-2.5 py-1 rounded-full bg-cp-primary/15 text-cp-primary-light border border-cp-primary/30">
          <Crown className="h-3 w-3" /> Plano {MOCK_USER.plan}
        </div>

        <div className="w-full mt-6 pt-6 border-t border-white/[0.06] space-y-3 text-left">
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500">Membro desde</span>
            <span className="text-slate-300 font-medium">{MOCK_USER.joined}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500">Empresa</span>
            <span className="text-slate-300 font-medium">{MOCK_USER.company}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500">Cargo</span>
            <span className="text-slate-300 font-medium">{MOCK_USER.role}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500">Status</span>
            <span className="inline-flex items-center gap-1.5 text-cp-primary-light font-medium">
              <span className="h-1.5 w-1.5 rounded-full bg-cp-primary animate-pulse" />
              Ativa
            </span>
          </div>
        </div>

        <button className="w-full mt-6 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/5 border border-red-500/20 rounded-lg py-2.5 transition inline-flex items-center justify-center gap-2">
          <LogOut className="h-4 w-4" /> Sair
        </button>
      </div>

      {/* Sections */}
      <div className="lg:col-span-2 space-y-4">
        {/* Plano usage */}
        <div className="rounded-2xl border border-cp-primary/30 bg-gradient-to-br from-cp-primary/[0.06] to-transparent p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-cp-primary-light" />
              <div className="font-heading font-semibold">Uso este mês</div>
            </div>
            <Link
              to="#"
              className="text-xs text-cp-primary-light hover:text-white inline-flex items-center gap-1"
            >
              Ver detalhes <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "PIX monitorados", val: 230, max: 500 },
              { label: "Mensagens", val: 920, max: 2000 },
              { label: "Integrações", val: 2, max: 3 },
              { label: "WhatsApps", val: 1, max: 2 },
            ].map((u, i) => (
              <div key={i}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">
                  {u.label}
                </div>
                <div className="text-lg font-bold">
                  {u.val}
                  <span className="text-slate-500 text-sm font-normal"> / {u.max}</span>
                </div>
                <div className="h-1 bg-cp-elevated/40 rounded-full mt-1.5">
                  <div
                    className="h-full bg-cp-primary rounded-full"
                    style={{ width: `${(u.val / u.max) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick actions */}
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { icon: User, title: "Dados pessoais", desc: "Nome, email, empresa" },
            { icon: Lock, title: "Senha & Segurança", desc: "Alterar senha, 2FA" },
            { icon: CreditCard, title: "Plano & Cobrança", desc: "Trocar plano, faturas" },
            { icon: Bell, title: "Notificações", desc: "Email, WhatsApp" },
            { icon: KeyRound, title: "API & Webhooks", desc: "Tokens e integrações" },
            { icon: Globe, title: "Idioma & Região", desc: "Português · BRL" },
          ].map((a, i) => {
            const Icon = a.icon;
            return (
              <div
                key={i}
                className="rounded-xl border border-white/[0.06] bg-cp-card/40 p-4 hover:border-cp-primary/20 transition cursor-pointer group flex items-center gap-3"
              >
                <div className="h-9 w-9 rounded-lg bg-cp-elevated/40 border border-white/[0.06] flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-cp-primary-light" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{a.title}</div>
                  <div className="text-[11px] text-slate-500 truncate">{a.desc}</div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-600 group-hover:text-cp-primary-light transition" />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PÁGINA — Switcher entre os 4 designs
   ============================================================ */
const DESIGNS = [
  {
    id: 1,
    key: "tabs",
    name: "Classic Tabs",
    tagline: "Sidebar com seções + conteúdo. Estilo Notion / Linear.",
    Component: DesignClassicTabs,
    pros: "Organizado, escala bem com mais seções, fácil navegar",
  },
  {
    id: 2,
    key: "header",
    name: "Profile Header + Cards",
    tagline: "Header grande com avatar + grid de cards. Estilo GitHub / Vercel.",
    Component: DesignProfileHeader,
    pros: "Identidade visual forte, foto em destaque, mais visual",
    recommended: true,
  },
  {
    id: 3,
    key: "linear",
    name: "Minimal Linear",
    tagline: "Tudo numa página, linhas separadas. Estilo Stripe / Linear.",
    Component: DesignMinimalLinear,
    pros: "Denso, profissional, fácil de escanear, sem clicks extras",
  },
  {
    id: 4,
    key: "split",
    name: "Split Dashboard",
    tagline: "Card de identidade + uso do plano + ações rápidas. Estilo Discord.",
    Component: DesignDashboardStyle,
    pros: "Mostra o plano em destaque, ótimo p/ saas com limites",
  },
];

export default function AccountOptions() {
  const [active, setActive] = useState(2);
  const Active = DESIGNS.find((d) => d.id === active);
  const ActiveComponent = Active.Component;

  return (
    <div className="min-h-screen bg-cp-bg text-slate-100" data-testid="account-options-page">
      {/* Header */}
      <header className="border-b border-white/[0.06] sticky top-0 bg-cp-bg/80 backdrop-blur-xl z-20">
        <div className="max-w-[1400px] mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="account-back-link"
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition"
          >
            <ArrowLeft size={16} />
            Voltar
          </Link>
          <div className="flex items-center gap-2.5">
            <LogoIcon size={28} />
            <span className="text-xs font-mono text-cp-primary-light tracking-wider">
              _MINHA_CONTA / DESIGN_OPTIONS
            </span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-12 max-w-[1400px] mx-auto">
        <div className="max-w-3xl">
          <span className="inline-block text-xs font-mono tracking-wider text-cp-primary-light mb-4">
            _ESCOLHA O LAYOUT
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4 leading-[1.05]">
            4 conceitos de "Minha Conta". <br />
            <span className="text-zinc-500">Qual combina mais com a ConvertPix?</span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 leading-relaxed">
            Clique em cada um pra preview ao vivo. Depois me diz o número que mais
            agradou — eu aplico em <code className="text-white">/app/settings</code> e
            adiciono os links no menu.
          </p>
        </div>
      </section>

      {/* Switcher */}
      <section className="px-6 max-w-[1400px] mx-auto sticky top-16 bg-cp-bg z-10 py-4 border-b border-white/[0.04]">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {DESIGNS.map((d) => {
            const isActive = active === d.id;
            return (
              <button
                key={d.id}
                onClick={() => setActive(d.id)}
                data-testid={`design-tab-${d.key}`}
                className={`relative text-left rounded-xl border p-4 transition ${
                  isActive
                    ? "border-cp-primary/60 bg-cp-primary/[0.06] shadow-lg shadow-cp-primary/10"
                    : "border-white/[0.06] bg-cp-card/30 hover:border-white/[0.15]"
                }`}
              >
                {d.recommended && (
                  <span className="absolute -top-2 right-3 px-1.5 py-0.5 rounded-full bg-cp-primary text-cp-bg text-[8px] font-bold tracking-widest uppercase">
                    Recomendado
                  </span>
                )}
                <div className="flex items-start justify-between mb-2">
                  <span className="font-mono text-[10px] text-slate-500 tracking-widest">
                    #{String(d.id).padStart(2, "0")}
                  </span>
                  {isActive && (
                    <Check className="h-4 w-4 text-cp-primary-light" />
                  )}
                </div>
                <div className="font-heading font-bold text-base mb-1">{d.name}</div>
                <div className="text-[11px] text-slate-500 leading-relaxed line-clamp-2">
                  {d.tagline}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Preview */}
      <section className="px-6 py-10 max-w-[1400px] mx-auto">
        <div className="mb-5 flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight">
              {Active.name}
            </h2>
            <p className="text-sm text-slate-400 mt-1">{Active.tagline}</p>
          </div>
          <div className="text-xs text-slate-500">
            <span className="text-cp-primary-light font-medium">Pontos fortes:</span>{" "}
            {Active.pros}
          </div>
        </div>

        {/* Window mockup */}
        <div className="rounded-2xl border border-white/[0.06] bg-cp-bg/60 overflow-hidden">
          <div className="bg-cp-elevated/30 border-b border-white/[0.06] px-4 py-2.5 flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-red-500/60" />
            <span className="h-2.5 w-2.5 rounded-full bg-cp-warning/60" />
            <span className="h-2.5 w-2.5 rounded-full bg-cp-primary/60" />
            <span className="ml-3 text-[11px] font-mono text-slate-500">
              convertpix.com/app/settings
            </span>
          </div>
          <div className="p-6 md:p-8">
            <ActiveComponent />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-20 max-w-[1400px] mx-auto">
        <div className="rounded-2xl border border-cp-primary/30 bg-gradient-to-br from-cp-primary/10 via-transparent to-transparent p-8 sm:p-10">
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-3">
            Qual layout te chamou mais?
          </h2>
          <p className="text-slate-400 mb-2 max-w-2xl">
            Me passa o <span className="text-white font-semibold">número</span> (01 a 04) ou o nome.
            Aplico em todo o painel — formulários reais, dados do tenant, alteração de senha,
            uso do plano em tempo real, etc.
          </p>
          <p className="text-xs text-slate-500">
            Posso também combinar elementos (ex: header do #2 + linhas do #3).
          </p>
        </div>
      </section>
    </div>
  );
}
